import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface OrganizationProfileNotification {
  id: string;
  user_id: string;
  organization_id: string;
  notification_type: string;
  status: string;
  reminder_count: number;
  created_at: string;
  completed_at?: string;
  last_reminder_sent?: string;
}

export const useOrganizationProfileNotifications = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["organization-profile-notifications", user?.id],
    queryFn: async (): Promise<OrganizationProfileNotification[]> => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('organization_profile_notifications')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching organization profile notifications:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!user,
    staleTime: 30000, // 30 seconds
  });
};

export const useOrganizationProfileCompleteness = (organizationId: string) => {
  return useQuery({
    queryKey: ["organization-profile-completeness", organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase.rpc(
        'check_organization_profile_completeness',
        { org_id: organizationId }
      );

      if (error) {
        console.error('Error checking organization profile completeness:', error);
        throw error;
      }

      return data;
    },
    enabled: !!organizationId,
    staleTime: 60000, // 1 minute
  });
};

export const useDismissOrganizationProfileNotification = () => {
  return async (notificationId: string) => {
    const { error } = await supabase
      .from('organization_profile_notifications')
      .update({ status: 'dismissed' })
      .eq('id', notificationId);

    if (error) {
      console.error('Error dismissing organization profile notification:', error);
      throw error;
    }
  };
};